import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, FileText, ListChecks, Cpu, HardDrive, RefreshCw, AlertTriangle, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const dash = (v) => (v === null || v === undefined ? '—' : v);
const num = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('da-DK'));
const usd = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
function bytes(b) {
    if (b === null || b === undefined) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
    return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

function Stat({ icon: Icon, label, value, sub }) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
                    <Icon className="h-4 w-4" /> {label}
                </div>
                <div className="text-2xl font-bold mt-1">{value}</div>
                {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
            </CardContent>
        </Card>
    );
}

export default function AdminDashboardPage() {
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try { setD((await adminAPI.dashboard()).data); }
        catch (e) { toast.error(e.response?.data?.detail || 'Could not load dashboard'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // AI usage by firm (visibility only)
    const cm = (() => { const x = new Date(); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; })();
    const [period, setPeriod] = useState(cm);
    const [usage, setUsage] = useState(null);
    const [openFirm, setOpenFirm] = useState(null);
    useEffect(() => {
        let active = true;
        adminAPI.usageByFirm(period).then((r) => { if (active) setUsage(r.data); }).catch(() => { if (active) setUsage(null); });
        return () => { active = false; };
    }, [period]);

    const q = d?.queue || {};
    const failed = (q.failed || 0);

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <LayoutDashboard className="h-7 w-7 text-primary" /> Platform admin
                    </h1>
                    <p className="text-muted-foreground mt-1">Live overview across all firms and clients. Read-only.</p>
                </div>
                <Button variant="ghost" size="icon" onClick={load} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            </div>

            {!d ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">{loading ? 'Loading…' : 'No data'}</CardContent></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Stat icon={Building2} label="Firms" value={num(d.firms)} />
                        <Stat icon={Building2} label="Clients" value={num(d.clients?.total)} sub={`${num(d.clients?.active)} active · ${num(d.clients?.archived)} archived`} />
                        <Stat icon={Users} label="Users" value={num(d.users?.total)} sub={Object.entries(d.users?.by_role || {}).map(([r, n]) => `${n} ${r.replace('_', ' ')}`).join(' · ')} />
                        <Stat icon={FileText} label="Invoices" value={num(d.documents?.total)} sub="documents (excl. files/bank)" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Card>
                            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ListChecks className="h-4 w-4" /> Processing queue (Part B)</CardTitle></CardHeader>
                            <CardContent className="flex flex-wrap gap-2 text-sm">
                                <Badge variant="success">done {num(q.done)}</Badge>
                                <Badge variant="outline">pending {num(q.pending)}</Badge>
                                <Badge variant="outline">processing {num(q.processing)}</Badge>
                                <Badge variant="outline" className={failed ? 'border-red-400 text-red-700' : ''}>failed {num(q.failed)}</Badge>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Invoices by status</CardTitle></CardHeader>
                            <CardContent className="flex flex-wrap gap-2 text-sm">
                                {Object.keys(d.documents?.by_status || {}).length === 0 ? <span className="text-muted-foreground">—</span> :
                                    Object.entries(d.documents.by_status).map(([s, n]) => (
                                        <Badge key={s} variant="outline" className={['failed', 'ai_processing_failed', 'unreadable'].includes(s) ? 'border-red-400 text-red-700' : ''}>{s} {n}</Badge>
                                    ))}
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Stat icon={Cpu} label="AI tokens — today" value={num(d.tokens?.today?.total_tokens)} sub={`est. ${usd(d.tokens?.today?.cost_usd)}`} />
                        <Stat icon={Cpu} label="AI tokens — this month" value={num(d.tokens?.month?.total_tokens)} sub={`est. ${usd(d.tokens?.month?.cost_usd)} (gpt-4o list price)`} />
                        <Stat icon={HardDrive} label="Document storage" value={bytes(d.storage?.bytes_approx)} sub={d.storage?.document_count != null ? `${num(d.storage.document_count)} files (approx)` : '—'} />
                    </div>

                    {/* AI usage by firm (visibility only — no caps) */}
                    <Card>
                        <CardHeader className="pb-2 flex flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2"><Cpu className="h-4 w-4" /> AI usage by firm</CardTitle>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Period</span>
                                <input type="month" value={period} onChange={(e) => setPeriod(e.target.value || cm)} className="h-8 rounded-md border px-2 text-sm" data-testid="usage-period" />
                            </div>
                        </CardHeader>
                        <CardContent className="overflow-x-auto">
                            {!usage ? <p className="text-sm text-muted-foreground py-3">Loading…</p> : (
                                <table className="w-full text-sm" data-testid="usage-by-firm">
                                    <thead>
                                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                                            <th className="py-2 pr-3">Firm</th>
                                            <th className="py-2 pr-3 text-right">Tokens</th>
                                            <th className="py-2 pr-3 text-right">Est. cost</th>
                                            <th className="py-2 pr-3 text-right">Extractions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usage.firms.length === 0 && usage.unattributed.total_tokens === 0 ? (
                                            <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No AI usage in this period.</td></tr>
                                        ) : usage.firms.map((f) => (
                                            <React.Fragment key={f.firm_id}>
                                                <tr className="border-b last:border-0 cursor-pointer hover:bg-muted/30" onClick={() => setOpenFirm(openFirm === f.firm_id ? null : f.firm_id)} data-testid={`usage-firm-${f.firm_id}`}>
                                                    <td className="py-2 pr-3 font-medium flex items-center gap-1">
                                                        {openFirm === f.firm_id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                                        {f.firm_name}
                                                    </td>
                                                    <td className="py-2 pr-3 text-right font-mono">{num(f.total_tokens)}</td>
                                                    <td className="py-2 pr-3 text-right font-mono">{usd(f.cost_usd)}</td>
                                                    <td className="py-2 pr-3 text-right font-mono">{num(f.extraction_count)}</td>
                                                </tr>
                                                {openFirm === f.firm_id && f.clients.map((c) => (
                                                    <tr key={c.tenant_id} className="border-b last:border-0 bg-muted/20">
                                                        <td className="py-1.5 pr-3 pl-6 text-muted-foreground">{c.client_name}</td>
                                                        <td className="py-1.5 pr-3 text-right font-mono text-muted-foreground">{num(c.total_tokens)}</td>
                                                        <td className="py-1.5 pr-3 text-right font-mono text-muted-foreground">{usd(c.cost_usd)}</td>
                                                        <td className="py-1.5 pr-3 text-right font-mono text-muted-foreground">{num(c.extraction_count)}</td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                        {usage.unattributed.total_tokens > 0 && (
                                            <tr className="border-b last:border-0 text-amber-700">
                                                <td className="py-2 pr-3 italic">Unattributed (no firm)</td>
                                                <td className="py-2 pr-3 text-right font-mono">{num(usage.unattributed.total_tokens)}</td>
                                                <td className="py-2 pr-3 text-right font-mono">{usd(usage.unattributed.cost_usd)}</td>
                                                <td className="py-2 pr-3 text-right font-mono">{num(usage.unattributed.extraction_count)}</td>
                                            </tr>
                                        )}
                                        <tr className="font-semibold border-t-2">
                                            <td className="py-2 pr-3">Total</td>
                                            <td className="py-2 pr-3 text-right font-mono">{num(usage.totals.total_tokens)}</td>
                                            <td className="py-2 pr-3 text-right font-mono">{usd(usage.totals.cost_usd)}</td>
                                            <td className="py-2 pr-3 text-right font-mono">{num(usage.totals.extraction_count)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">Click a firm to see its per-client breakdown. Cost is an estimate (gpt-4o list price). Visibility only — no usage caps are enforced.</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2"><CardTitle className="text-base">Quick links</CardTitle></CardHeader>
                        <CardContent className="flex flex-wrap gap-2">
                            <Link to="/app/admin/firms"><Button variant="outline" size="sm"><Building2 className="h-4 w-4 mr-1" /> Firms & Billing <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
                            <Link to="/app/admin/activity"><Button variant="outline" size="sm">Activity & Audit <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
                            {failed > 0 && (
                                <Link to="/app/admin/firms"><Button variant="outline" size="sm" className="border-red-400 text-red-700"><AlertTriangle className="h-4 w-4 mr-1" /> {failed} failed job(s) — review</Button></Link>
                            )}
                        </CardContent>
                    </Card>

                    <p className="text-xs text-muted-foreground">
                        All figures are read from live data. AI cost is an estimate from logged token usage at gpt-4o list prices. Storage is approximate (from stored document bytes). “—” means a metric isn’t tracked.
                    </p>
                </>
            )}
        </div>
    );
}
